import { Component } from '@angular/core';
import { Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import { filter, map } from 'rxjs/operators';
import { checkToken, deleteToken, selectLoaderCount, selectPublicLoaderCount, selectUserInformation, UserAuthenticationService, UserToken } from 'sp-arch-lib';
import { environment } from 'sp-mark-src/environments/environment';
import { publicRoutes } from 'sp-mark/app-routing.module';
import * as bootActions from 'sp-mark/feature-modules/boot/redux/boot.actions';
import * as bootSelectors from 'sp-mark/feature-modules/boot/redux/index';
import { AppState } from 'sp-mark/redux';
import * as routerSelectors from 'sp-mark/redux/router/index';
import * as routerActions from 'sp-mark/redux/router/router.actions';
import { closeDialog, LocalUnit, LoginData, MenuItem, MenuSectionItem, openDialog } from 'sp-ui-lib';
import { routingPathsConstants } from './models/routing-paths.constants';

@Component({
  selector: 'sp-root',
  templateUrl: './app.component.html'
})
export class AppComponent {
  title = 'sp-market';
  env = environment.context;
  menuSectionItems$: Observable<MenuSectionItem[]> = this.appState$.select(routerSelectors.selectMenuSectionItems);

  loaderActive$: Observable<boolean> = this.appState$.select(selectLoaderCount)
    .pipe(
      map(loaderCount => loaderCount > 0)
    );

  publicLoaderActive$: Observable<boolean> = this.appState$.select(selectPublicLoaderCount).pipe(
    map(loaderCount => loaderCount > 0)
  );
  userInfoFromToken$: Observable<UserToken> = this.appState$.select(selectUserInformation);
  localUnitsDisabled$: Observable<boolean> = this.appState$.select(bootSelectors.selectLocalUnitsDisabled);
  localUnitsCompletlyDisabled$: Observable<boolean> = this.appState$.select(bootSelectors.selectLocalUnitsCompletlyDisabled);
  username$: Observable<string> = this.appState$.select(selectUserInformation).pipe(
    filter(userToken => !!userToken),
    map(userToken => userToken.sub)
  );
  tokenAuthority$: Observable<string> = this.appState$.select(selectUserInformation).pipe(
    filter(userToken => !!userToken),
    map(userToken => userToken.roles[0])
  );
  localUnits$: Observable<LocalUnit[]> = this.appState$.select(bootSelectors.selectLocalUnits);
  localStorageUsername: string = window.localStorage.hasOwnProperty('smallPayUsername')
    ? window.localStorage.getItem('smallPayUsername')
    : '';

  get isPublicRoute() {
    return publicRoutes.some(x => window.location.hash.includes(x));
  }

  get isBootPage() {
    return window.location.hash.includes('boot');
  }

  constructor(private appState$: Store<AppState>, private readonly userTokenService: UserAuthenticationService) {
    this.appState$.dispatch(checkToken());

    this.checkRedirectToMainPage();

    if (this.userTokenService.isUserLogged()) {
      this.appState$.dispatch(bootActions.mandatoryData());
    }
  }

  checkRedirectToMainPage() {
    if (!this.isPublicRoute) {
      const hash = window.location.hash;
      if (hash) {
        const page = hash.split('/')[1];
        if (page !== 'dashboard' && page !== 'boot') {
          this.appState$.dispatch(routerActions.navigateTo(routingPathsConstants.DASHBOARD));
        }
      }
    }
  }

  menuItemSelected(item: MenuItem) {
    if (item.id !== 'products_catalog' && item.id !== 'sell') {
      this.appState$.dispatch(bootActions.toggleLocalUnitsDisable({ flagLocalUnitsDisabled: false }));
      this.appState$.dispatch(bootActions.toggleLocalUnitsCompletlyDisable({ flagLocalUnitsCompletlyDisabled: false }));
    }
    this.appState$.dispatch(routerActions.navigateTo({ id: item.id, path: item.path }));
  }

  doLogin(loginData: LoginData) {
    this.appState$.dispatch(bootActions.doLogin({ loginData }));
  }

  openLogoutDialog() {
    this.appState$.dispatch(
      openDialog({
        dialogData: {
          title: 'SHARED.DIALOG.ALERT_TITLE',
          message: ['SHARED.DIALOG.LOGOUT_CHECK'],
          buttons: [
            {
              label: 'SHARED.BUTTON.ROLLBACK',
              icon: '',
              callback: (() => this.appState$.dispatch(closeDialog())),
            },
            {
              label: 'SHARED.BUTTON.CONFIRM',
              icon: '',
              callback: (() => {
                this.appState$.dispatch(routerActions.navigateTo(routingPathsConstants.LOGIN));
                this.appState$.dispatch(deleteToken());
                this.appState$.dispatch(closeDialog());
              })
            }
          ]
        }
      })
    );
  }

  toggleSelectedUnit(unit: LocalUnit) {
    this.appState$.dispatch(bootActions.toggleLocalUnit({ localUnit: unit }));
  }

  toggleAllUnits() {
    this.appState$.dispatch(bootActions.toggleAllLocalUnits());
  }
}
